import React from 'react';
import { Sparkles } from 'lucide-react';

const baseActionClasses = 'inline-flex items-center justify-center gap-1.5 rounded-lg border py-2 text-[12px] font-medium transition-colors active:scale-[0.98]';
const actionVariants = {
  default: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-900 dark:hover:text-white',
  magic: 'border-brand-200/70 dark:border-brand-800/60 bg-brand-50/50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/30 hover:border-brand-300 dark:hover:border-brand-700 hover:text-brand-800 dark:hover:text-brand-200',
};

export function LeadCardShell({ children, actions }) {
  return (
    <div className="group relative h-full flex flex-col rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800/40 transition-all duration-200 hover:border-brand-300/70 dark:hover:border-brand-700/60 hover:shadow-lg hover:shadow-brand-100/50 dark:hover:shadow-brand-950/30 hover:-translate-y-0.5 overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="flex-1 p-5 flex flex-col">{children}</div>
      {actions ? (
        <div className="px-5 pb-4 pt-3 mt-auto border-t border-gray-100 dark:border-gray-700/60 flex gap-1.5">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export function EntityAvatar({ name, initials, colorClass, imageUrl, shape = 'circle', alt }) {
  const roundedClass = shape === 'square' ? 'rounded-xl' : 'rounded-full';
  const [imageVisible, setImageVisible] = React.useState(Boolean(imageUrl));

  React.useEffect(() => {
    setImageVisible(Boolean(imageUrl));
  }, [imageUrl]);

  return (
    <div className="relative flex-shrink-0">
      {imageVisible ? (
        <img
          src={imageUrl}
          alt={alt || name}
          onError={() => setImageVisible(false)}
          className={`w-11 h-11 ${roundedClass} ${shape === 'square' ? 'object-contain p-1.5' : 'object-cover'} ring-2 ring-white dark:ring-gray-800 shadow-sm bg-white`}
        />
      ) : (
        <div
          className={`w-11 h-11 ${roundedClass} ${colorClass} flex items-center justify-center font-semibold text-[13px] ring-2 ring-white dark:ring-gray-800 shadow-sm`}
          aria-label={name}
        >
          {initials}
        </div>
      )}
    </div>
  );
}

export function InsightChip({ label, iconMap }) {
  const Icon = iconMap[label] || Sparkles;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 dark:bg-brand-900/30 border border-brand-100 dark:border-brand-800/60 px-1.5 py-0.5 text-[11px] font-medium text-brand-700 dark:text-brand-200">
      <Icon className="w-3 h-3 text-brand-500 dark:text-brand-400" strokeWidth={2} />
      <span>{label}</span>
    </span>
  );
}

export function MetaDot() {
  return <span className="text-gray-300 dark:text-gray-600 select-none">·</span>;
}

export function CardActionButton({
  children,
  icon: Icon,
  onClick,
  title,
  ariaLabel,
  variant = 'default',
  iconClassName = 'text-gray-400 dark:text-gray-500',
  flex = true,
  compact = false,
}) {
  const widthClass = flex ? 'flex-1 min-w-0' : '';
  const paddingClass = compact ? 'px-2.5' : 'px-3';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className={`${widthClass} ${baseActionClasses} ${actionVariants[variant]} ${paddingClass}`}
    >
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${iconClassName}`} strokeWidth={2} />
      {children && <span className="truncate">{children}</span>}
    </button>
  );
}
